import type { RunPreparationSummary, SubmissionBatch, SubmissionProgress } from "@nova-ai/shared"
import { ChevronDown } from "lucide-react"

import { humanizeEnumLabel } from "../../lib/enum-label"
import { formatDateTime } from "../../lib/format-date"
import { Button } from "../ui/button"
import { Table, type TableColumn } from "../ui/table"
import { RunCatalogProducts } from "./run-catalog-evidence"
import { RunPreparationStatus } from "./run-preparation-status"

const PRODUCT_COLUMNS: TableColumn<SubmissionBatch["products"][number]>[] = [
  {
    key: "sku",
    header: "Product",
    render: (product) => <span className="font-mono break-all">{product.sku}</span>,
  },
  {
    key: "status",
    header: "Result",
    render: (product) =>
      product.status === "verified" ? "Import confirmed" : humanizeEnumLabel(product.status),
  },
  {
    key: "message",
    header: "Details",
    render: (product) => (
      <span className="break-words">{product.message ?? "No further details"}</span>
    ),
  },
]

export function RunSubmissionDetails({
  progress,
  preparation,
  onHistoryPage,
}: {
  progress: SubmissionProgress
  onHistoryPage?: (offset: number) => void
  preparation: RunPreparationSummary | null | undefined
}) {
  return (
    <details className="group/submission border-t border-border/70 pt-3">
      <summary className="flex w-fit cursor-pointer list-none items-center gap-2 rounded-full text-xs font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <ChevronDown
          aria-hidden
          className="size-3.5 transition-transform group-open/submission:rotate-180"
        />
        Preparation and submission details
      </summary>

      <div className="mt-3 max-h-[40vh] space-y-4 overflow-y-auto pr-2 text-xs">
        <StageDetails progress={progress} />

        {preparation && (
          <div className="space-y-2">
            <p className="text-muted-foreground">
              {`Preparation snapshot: ${formatDateTime(preparation.updatedAt)}`}
            </p>

            <RunPreparationStatus preparation={preparation} />
          </div>
        )}

        <BatchHistory progress={progress} />

        {onHistoryPage && progress.batchPage && (
          <nav aria-label="Batch history pages" className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={progress.batchPage.offset === 0}
              onClick={() => onHistoryPage(Math.max(0, (progress.batchPage?.offset ?? 0) - 50))}
            >
              Previous batches
            </Button>

            <Button
              size="sm"
              disabled={progress.batchPage.nextOffset === null}
              onClick={() => {
                if (progress.batchPage?.nextOffset != null) {
                  onHistoryPage(progress.batchPage.nextOffset)
                }
              }}
            >
              Next batches
            </Button>
          </nav>
        )}

        <p className="text-muted-foreground">
          Import confirmation, catalog publication, and image coverage are separate evidence. Offers
          and storefront visibility need separate checks.
        </p>
      </div>
    </details>
  )
}

function StageDetails({ progress }: { progress: SubmissionProgress }) {
  return (
    <dl className="space-y-2">
      {Object.entries(progress.stages).map(([key, stage]) => (
        <div key={key} className="flex flex-wrap gap-x-2 gap-y-1">
          <dt className="font-medium text-foreground">{humanizeEnumLabel(key)}</dt>

          <dd className="min-w-0 break-words text-muted-foreground">
            {stage.detail ?? "No further evidence recorded"}
          </dd>
        </div>
      ))}
    </dl>
  )
}

function BatchHistory({ progress }: { progress: SubmissionProgress }) {
  if (!progress.historyAvailable) {
    return <p className="text-muted-foreground">Historical submission details are unavailable.</p>
  }
  if (progress.batches.length === 0) {
    return <p className="text-muted-foreground">No batches sent yet.</p>
  }
  return (
    <ol aria-label="Submission batches" className="space-y-3">
      {progress.batches.map((batch) => (
        <li key={batch.id} className="space-y-2 rounded-lg bg-muted/40 p-3">
          <BatchEvidence batch={batch} />

          <RunCatalogProducts batch={batch} />

          {batch.products.length > 0 ? (
            <details>
              <summary className="w-fit cursor-pointer rounded-full font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {`Affected products (${batch.products.length.toLocaleString("en-US")})`}
              </summary>

              <div className="mt-2">
                <Table
                  columns={PRODUCT_COLUMNS}
                  rows={batch.products}
                  rowKey={(product) => product.sku}
                  dense
                  embedded
                  stackOnMobile
                />
              </div>
            </details>
          ) : (
            <p className="text-muted-foreground">Product-level evidence is unavailable.</p>
          )}
        </li>
      ))}
    </ol>
  )
}

function BatchEvidence({ batch }: { batch: SubmissionBatch }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-medium text-foreground">{batch.label}</span>

        <span className="text-muted-foreground">
          {batch.status === "verified" ? "Import confirmed" : humanizeEnumLabel(batch.status)}
        </span>
      </div>

      <p className="text-muted-foreground tabular-nums">
        {`${batch.sent ?? "Unknown"} sent · ${batch.confirmed ?? "Unknown"} import confirmed · ${batch.rejected ?? "Unknown"} rejected · ${batch.unresolved ?? "Unknown"} unresolved`}
      </p>

      {batch.submittedAt && (
        <p className="text-muted-foreground">{`Sent ${formatDateTime(batch.submittedAt)}`}</p>
      )}

      {batch.observedAt && (
        <p className="text-muted-foreground">{`Observed ${formatDateTime(batch.observedAt)}`}</p>
      )}

      {batch.error && <p className="break-words font-medium text-foreground">{batch.error}</p>}
    </>
  )
}
